import Mustache from 'mustache';
import i18n from '../utils/i18n.js';
import toast from '../utils/toast.js';
import { hideDropdown, translateDayText, formatVolume } from '../utils/utils.js';
import { trackAddToCart, trackSearch, trackViewItem, trackTopupClick } from '../utils/analytics.js';

/**
 * Initializes eSIM module
 * @param {Object} store - Store instance
 * @param {Object} sharedElements - Shared DOM elements
 * @param {HTMLElement} sharedElements.sidebarOverlay - Sidebar overlay element
 * @param {Object} callbacks - Callback functions
 * @param {Function} callbacks.renderCart - Render cart callback
 * @param {Function} callbacks.openCart - Open cart callback
 * @returns {Object} eSIM module API
 */
export function initEsim(store, sharedElements, callbacks) {
    const searchInput = document.getElementById('locationSearch');
    const dropdown = document.getElementById('autocompleteDropdown');
    const tagsContainer = document.getElementById('tagsContainer');
    const plansSelectionSection = document.getElementById('plansSelectionSection');
    const matchingPlansSection = document.getElementById('matchingPlansSection');
    const matchingPlansContainer = document.getElementById('matchingPlansContainer');
    const durationSection = document.getElementById('durationSection');
    const durationContainer = document.getElementById('durationContainer');
    const dataPackagesSection = document.getElementById('dataPackagesSection');
    const dataPackagesContainer = document.getElementById('dataPackagesContainer');
    const packageDetailsSidebar = document.getElementById('packageDetailsSidebar');
    const packageDetailsBody = document.getElementById('packageDetailsBody');
    const topupPlansSection = document.getElementById('topupPlansSection');
    const topupPlansContainer = document.getElementById('topupPlansContainer');
    const favoritesSection = document.getElementById('favoritesSection');
    const favoritesContainer = document.getElementById('favoritesContainer');

    const {
        sidebarOverlay
    } = sharedElements;

    const {
        renderCart,
        openCart
    } = callbacks;


    /**
     * Loads countries based on current language
     */
    async function loadCountries() {
        try {
            const isSpanish = i18n.getLanguage() === 'es';
            if (isSpanish) {
                await store.loadCountriesFromJson();
                setTimeout(async () => {
                    try {
                        await store.loadAllLocations();
                    } catch (error) {
                        console.error('Error loading locations (delayed):', error);
                    }
                }, 1000);
            } else {
                await store.loadAllLocations();
            }
        } catch (error) {
            console.error('Error loading countries:', error);
        }
    }

    /**
     * Loads and displays favorite plans
     */
    async function loadFavorites() {
        try {
            const favoritesResponse = await store.provider.favorites();
            
            if (!favoritesResponse.success || !favoritesResponse.obj?.packageList || favoritesResponse.obj.packageList.length === 0) {
                if (favoritesSection) {
                    favoritesSection.classList.add('hidden');
                }
                return;
            }

            const favoritePlans = favoritesResponse.obj.packageList;
            renderFavorites(favoritePlans);
        } catch (error) {
            console.error('Error loading favorites:', error);
            if (favoritesSection) {
                favoritesSection.classList.add('hidden');
            }
        }
    }

    /**
     * Renders favorite plans cards
     * @param {Array} plans - Array of favorite plan objects
     */
    function renderFavorites(plans) {
        if (!favoritesContainer || !favoritesSection) return;

        favoritesContainer.innerHTML = '';
        const template = document.getElementById('template-favorite-card');
        if (!template) return;

        const templateHtml = template.innerHTML;

        // Sort plans by price ascending
        const sortedPlans = [...plans].sort((a, b) => (a.price || 0) - (b.price || 0));

        sortedPlans.forEach(plan => {
            const price = (plan.price / 10000).toFixed(2);
            const displayVolume = formatVolume(plan.volume);
            const locationCode = plan.locationCode || plan.location;
            const locationName = store.getLocationName(locationCode) || locationCode;
            const flagIcon = store.getFlagIcon(locationCode);
            
            // Translate duration unit
            let durationUnit = plan.durationUnit?.toLowerCase() || 'days';
            durationUnit = translateDayText(durationUnit, plan.duration);

            const data = {
                packageCode: plan.packageCode,
                locationName: translateDayText(locationName),
                flagIcon: flagIcon,
                price: price,
                displayVolume: displayVolume,
                duration: plan.duration,
                durationUnit: durationUnit,
                allowsTopUp: plan.supportTopUpType === 2
            };

            const html = Mustache.render(templateHtml, data);
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = html;
            const card = tempDiv.firstElementChild;
            
            const addToCartButton = card.querySelector('.favorite-card-add-to-cart');
            const viewDetailsButton = card.querySelector('.favorite-card-view-details');

            addToCartButton.addEventListener('click', (e) => {
                e.stopPropagation();
                addPackageToCart(plan);
            });

            viewDetailsButton.addEventListener('click', (e) => {
                e.stopPropagation();
                trackViewItem(plan.packageCode, displayVolume);
                showPackageDetailsSidebar([plan], displayVolume);
            });

            favoritesContainer.appendChild(card);
        });

        i18n.updatePage();
        favoritesSection.classList.remove('hidden');
    }

    /**
     * Initializes eSIM module
     */
    async function init() {
        searchInput.disabled = true;
        searchInput.placeholder = i18n.t('loading');

        try {
            const loadPromises = [store.loadAllPlans(), loadCountries()];
            await Promise.all(loadPromises);

            // Load favorites after plans are loaded
            await loadFavorites();

            searchInput.disabled = false;
            searchInput.placeholder = i18n.t('searchPlaceholder');
        } catch (error) {
            console.error('Error initializing app:', error);
            searchInput.disabled = false;
            searchInput.placeholder = i18n.t('searchPlaceholder');
        }
    }

    /**
     * Adds country tag to selection
     * @param {Object} location - Location object
     */
    function addCountryTag(location) {
        if (store.addCountry(location)) {
            renderTags();
            filterAndDisplayPlans();
            searchInput.value = '';
            hideDropdown(dropdown);
            searchInput.focus();
        }
    }

    /**
     * Removes country tag from selection
     * @param {string} locationCode - Location code to remove
     */
    function removeCountryTag(locationCode) {
        const currentLocation = store.getCurrentLocation();
        if (currentLocation && currentLocation.plans) {
            const hasMatchingPlan = currentLocation.plans.some(plan => {
                const planLocationCode = plan.locationCode || plan.location;
                return planLocationCode === locationCode;
            });

            if (hasMatchingPlan) {
                store.clearCurrentLocation();
            }
        }

        store.removeCountry(locationCode);
        document.querySelectorAll('.location-plan-card').forEach(c => c.classList.remove('active'));
        document.querySelectorAll('.duration-card').forEach(c => c.classList.remove('active'));
        durationContainer.innerHTML = '';
        dataPackagesContainer.innerHTML = '';
        plansSelectionSection.classList.add('hidden');
        durationSection.classList.add('hidden');
        dataPackagesSection.classList.add('hidden');
        renderTags();
        filterAndDisplayPlans();
    }

    /**
     * Renders country tags
     */
    function renderTags() {
        tagsContainer.innerHTML = '';
        store.getSelectedCountries().forEach(location => {
            const template = document.getElementById('template-country-tag');
            const templateHtml = template.innerHTML;
            const data = store.prepareTagData(location);
            const html = Mustache.render(templateHtml, data);
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = html;
            const tag = tempDiv.firstElementChild;
            tag.querySelector('.tag-remove').addEventListener('click', () => removeCountryTag(location.code));
            tagsContainer.appendChild(tag);
        });
    }

    /**
     * Filters and displays plans based on selected countries
     */
    function filterAndDisplayPlans() {
        const plansByLocation = store.filterPlans();

        if (!plansByLocation) {
            plansSelectionSection.classList.add('hidden');
            matchingPlansSection.classList.add('hidden');
            return;
        }

        if (Object.keys(plansByLocation).length === 0) {
            const template = document.getElementById('template-empty-state');
            const templateHtml = template.innerHTML;
            const data = {
                icon: '📦',
                text: i18n.t('noPlansFound')
            };
            matchingPlansContainer.innerHTML = Mustache.render(templateHtml, data);
            plansSelectionSection.classList.remove('hidden');
            matchingPlansSection.classList.remove('hidden');
            return;
        }

        renderMatchingPlans(plansByLocation);
        plansSelectionSection.classList.remove('hidden');
        matchingPlansSection.classList.remove('hidden');
        durationSection.classList.add('hidden');
        dataPackagesSection.classList.add('hidden');
    }

    /**
     * Renders matching plans by location
     * @param {Object} plansByLocation - Plans grouped by location
     */
    function renderMatchingPlans(plansByLocation) {
        matchingPlansContainer.innerHTML = '';
        const template = document.getElementById('template-location-plan-card');
        const templateHtml = template.innerHTML;

        const plansData = store.prepareMatchingPlansData(plansByLocation);
        if (!plansData) return;

        plansData.forEach((data) => {
            const html = Mustache.render(templateHtml, data);
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = html;
            const card = tempDiv.firstElementChild;
            card.addEventListener('click', () => {
                document.querySelectorAll('.location-plan-card').forEach(c => c.classList.remove('active'));
                card.classList.add('active');
                selectLocation(data.locationData);
            });
            matchingPlansContainer.appendChild(card);
        });
    }

    /**
     * Selects location and shows duration options
     * @param {Object} locationData - Location data with plans
     */
    function selectLocation(locationData) {
        store.setCurrentLocation(locationData);
        const durations = store.prepareDurationsData(locationData);
        durationContainer.innerHTML = '';
        const template = document.getElementById('template-duration-card');
        const templateHtml = template.innerHTML;

        durations.forEach(duration => {
            const data = {
                duration: duration.duration,
                durationUnit: translateDayText(duration.durationUnit),
                display: translateDayText(duration.display, duration.duration),
                priceRange: duration.priceRange,
                packageCount: duration.packageCount,
                packageText: duration.packageText
            };

            const html = Mustache.render(templateHtml, data);
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = html;
            const card = tempDiv.firstElementChild;
            card.addEventListener('click', () => {
                document.querySelectorAll('.duration-card').forEach(c => c.classList.remove('active'));
                card.classList.add('active');
                selectDuration(duration.duration, duration.durationUnit);
            });
            durationContainer.appendChild(card);
        });

        durationSection.classList.remove('hidden');
        dataPackagesSection.classList.add('hidden');
        durationSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    /**
     * Selects duration and shows package options
     * @param {number} duration - Duration value
     * @param {string} unit - Duration unit
     */
    function selectDuration(duration, unit) {
        const packagesData = store.preparePackagesData(duration, unit);
        dataPackagesContainer.innerHTML = '';
        const template = document.getElementById('template-package-card');
        const templateHtml = template.innerHTML;

        packagesData.forEach(packageData => {

            const data = {
                volume: packageData.volume,
                priceRange: packageData.priceRange
            };

            const html = Mustache.render(templateHtml, data);
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = html;
            const card = tempDiv.firstElementChild;
            const addToCartButton = card.querySelector('.package-card-add-to-cart');
            const viewDetailsButton = card.querySelector('.package-card-view-details');

            addToCartButton.addEventListener('click', (e) => {
                e.stopPropagation();
                if (packageData.plans.length > 0) {
                    addPackageToCart(packageData.plans[0]);
                }
            });

            viewDetailsButton.addEventListener('click', (e) => {
                e.stopPropagation();
                if (packageData.plans.length > 0) {
                    const plan = packageData.plans[0];
                    trackViewItem(plan.packageCode, packageData.volume);
                }
                showPackageDetailsSidebar(packageData.plans, packageData.volume);
            });

            dataPackagesContainer.appendChild(card);
        });

        i18n.updatePage();

        dataPackagesSection.classList.remove('hidden');
        dataPackagesSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    /**
     * Shows package details sidebar
     * @param {Array} plans - Array of plan objects
     * @param {string} volume - Volume display text
     */
    function showPackageDetailsSidebar(plans, volume) {
        const packages = store.preparePackageDetailsData(plans);
        const template = document.getElementById('template-package-detail-card');
        const templateHtml = template.innerHTML;

        packageDetailsBody.innerHTML = '';

        // Track view_item for the first plan
        if (packages.length > 0 && packages[0].packageCode) {
            trackViewItem(packages[0].packageCode, volume);
        }

        packages.forEach(pkg => {
            pkg.name = translateDayText(pkg.name);
            pkg.durationUnit = translateDayText(pkg.durationUnit, pkg.duration);
            
            const html = Mustache.render(templateHtml, pkg);
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = html;
            const detailCard = tempDiv.firstElementChild;

            if (pkg.hasCountries) {
                const countriesSection = document.createElement('div');
                countriesSection.className = 'package-countries-section pt-2 mt-2';
                countriesSection.innerHTML = `<h4>${i18n.t('countriesIncluded')}</h4><div class="package-countries-list d-flex flex-wrap gap-1"></div>`;

                const countriesList = countriesSection.querySelector('.package-countries-list');
                const countryTagTemplate = document.getElementById('template-country-tag');
                const countryTagTemplateHtml = countryTagTemplate.innerHTML;

                pkg.countries.forEach(country => {
                    const data = {
                        code: country.code,
                        name: translateDayText(country.name),
                        flagIcon: country.flagIcon
                    };

                    const html = Mustache.render(countryTagTemplateHtml, data);
                    const tempDiv = document.createElement('div');
                    tempDiv.innerHTML = html;
                    const countryTag = tempDiv.firstElementChild;
                    const removeButton = countryTag.querySelector('.tag-remove');
                    if (removeButton) {
                        removeButton.remove();
                    }

                    countriesList.appendChild(countryTag);
                });

                detailCard.appendChild(countriesSection);
            }

            packageDetailsBody.appendChild(detailCard);
        });

        i18n.updatePage();

        sidebarOverlay.classList.remove('hidden');
        packageDetailsSidebar.classList.remove('hidden');
        document.body.style.overflow = 'hidden';
    }

    /**
     * Closes package details sidebar
     */
    function closePackageDetailsSidebarFunc() {
        packageDetailsSidebar.classList.add('hidden');
        document.body.style.overflow = '';
    }

    function showSuggestions(locations) {
        if (!locations.length) {
            hideDropdown(dropdown);
            return;
        }

        dropdown.innerHTML = '';
        const template = document.getElementById('template-autocomplete-item');
        const templateHtml = template.innerHTML;

        locations.forEach((location) => {
            const isRegional = location.type === 2;
            const subLocationCount = location.subLocationList ? location.subLocationList.length : 0;
            const typeText = isRegional ? `${i18n.t('region')} (${subLocationCount} ${i18n.t('country').toLowerCase()}s)` : i18n.t('country');

            const data = {
                name: location.name,
                code: location.code,
                typeText: typeText,
                flagIcon: store.getFlagIcon(location.code)
            };

            const html = Mustache.render(templateHtml, data);
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = html;
            const item = tempDiv.firstElementChild;
            item.addEventListener('click', () => {
                addCountryTag(location);
            });
            dropdown.appendChild(item);
        });

        dropdown.classList.remove('hidden');
    }

    /**
     * Adds package to cart
     * @param {Object} plan - Plan object
     */
    function addPackageToCart(plan) {
        const packageDetails = store.preparePackageDetailsData([plan])[0];

        const packageData = {
            packageCode: packageDetails.packageCode,
            name: translateDayText(packageDetails.name),
            price: packageDetails.price,
            charge: packageDetails.charge || 0,
            displayVolume: packageDetails.displayVolume,
            duration: packageDetails.duration,
            durationUnit: translateDayText(packageDetails.durationUnit, packageDetails.duration),
            hasSpeed: packageDetails.hasSpeed,
            speed: packageDetails.speed,
            smsStatus: packageDetails.smsStatus,
            dataType: packageDetails.dataType,
            activeType: packageDetails.activeType,
            operatorList: packageDetails.operatorList,
            supportTopUpType: packageDetails.supportTopUpType,
            originalPlan: plan
        };

        store.addToCart(packageData);
        trackAddToCart(packageData);
        renderCart();
        openCart();
    }

    /**
     * Sets up event listeners
     */
    function setupEventListeners() {
        searchInput.addEventListener('input', (e) => {
            const query = e.target.value.trim();

            if (query.length === 0) {
                hideDropdown(dropdown);
                return;
            }

            if (topupPlansSection && topupPlansContainer) {
                topupPlansContainer.innerHTML = '';
                topupPlansSection.classList.add('hidden');
            }

            if (favoritesSection) {
                favoritesSection.classList.add('hidden');
            }

            if (query.length >= 1) {
                const isSpanish = i18n.getLanguage() === 'es';
                const results = store.searchLocations(query, isSpanish);
                showSuggestions(results);
                trackSearch(query);
            }
        });

        document.addEventListener('click', (e) => {
            if (!searchInput.contains(e.target) && !dropdown.contains(e.target)) {
                hideDropdown(dropdown);
            }
        });
    }

    setupEventListeners();
    init();

    /**
     * Refreshes current sections based on store state
     */
    function refreshCurrentSections() {
        const currentLocation = store.getCurrentLocation();
        if (currentLocation) {
            selectLocation(currentLocation);
        } else {
            filterAndDisplayPlans();
        }
    }

    /**
     * Shows topup plans in the main container
     * @param {Array} plans - Array of topup plans
     * @param {string} esimTranNo - eSIM transaction number
     */
    /**
     * Shows topup plans in the main container
     * @param {Array} plans - Array of topup plans
     * @param {string} esimTranNo - eSIM transaction number
     */
    function showTopupPlans(plans, esimTranNo) {
        if (!store || !plans || plans.length === 0) {
            toast.error(i18n.t('noTopupPlansAvailable'));
            return;
        }

        if (!topupPlansSection || !topupPlansContainer) {
            console.error('Topup plans section elements not found');
            return;
        }

        trackTopupClick(esimTranNo);

        if (searchInput) {
            searchInput.value = '';
        }
        hideDropdown(dropdown);
        if (tagsContainer) {
            tagsContainer.innerHTML = '';
        }
        store.selectedCountries = [];
        store.clearCurrentLocation();
        
        if (plansSelectionSection) {
            plansSelectionSection.classList.add('hidden');
        }
        if (matchingPlansSection) {
            matchingPlansSection.classList.add('hidden');
        }
        if (durationSection) {
            durationSection.classList.add('hidden');
        }
        if (dataPackagesSection) {
            dataPackagesSection.classList.add('hidden');
        }
        
        const userEsimsSidebar = document.getElementById('userEsimsSidebar');
        if (userEsimsSidebar) {
            userEsimsSidebar.classList.add('hidden');
        }
        if (sidebarOverlay) {
            sidebarOverlay.classList.add('hidden');
        }
        document.body.style.overflow = '';

        const packages = store.preparePackageDetailsData(plans);
        const template = document.getElementById('template-topup-package-card');
        if (!template) {
            console.error('Topup package card template not found');
            return;
        }
        const templateHtml = template.innerHTML;

        topupPlansContainer.innerHTML = '';

        packages.forEach((pkg, index) => {
            pkg.name = translateDayText(pkg.name);
            pkg.durationUnit = translateDayText(pkg.durationUnit, pkg.duration);
            
            const data = {
                price: pkg.price,
                displayVolume: pkg.displayVolume,
                duration: pkg.duration,
                durationUnit: pkg.durationUnit,
                packageCode: pkg.packageCode
            };
            
            const html = Mustache.render(templateHtml, data);
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = html;
            const card = tempDiv.firstElementChild;
            
            const addToCartButton = card.querySelector('.topup-package-add-to-cart');
            const viewDetailsButton = card.querySelector('.topup-package-view-details');

            if (addToCartButton) {
                addToCartButton.addEventListener('click', (e) => {
                    e.stopPropagation();
                    if (!store) {
                        toast.error(i18n.t('storeNotAvailable'));
                        return;
                    }

                    const originalPlan = plans[index];
                    const packageData = {
                        packageCode: pkg.packageCode,
                        name: pkg.name,
                        price: parseFloat(pkg.price),
                        charge: parseFloat(pkg.charge) || 0,
                        displayVolume: pkg.displayVolume,
                        duration: pkg.duration,
                        durationUnit: pkg.durationUnit,
                        hasSpeed: pkg.hasSpeed,
                        speed: pkg.speed,
                        smsStatus: pkg.smsStatus,
                        dataType: pkg.dataType,
                        activeType: pkg.activeType,
                        operatorList: pkg.operatorList,
                        supportTopUpType: pkg.supportTopUpType,
                        esimTranNo: esimTranNo,
                        originalPlan: originalPlan
                    };
                    
                    store.addToCart(packageData);
                    trackAddToCart(packageData);
                    if (renderCart) {
                        renderCart();
                    }
                });
            }

            if (viewDetailsButton) {
                viewDetailsButton.addEventListener('click', (e) => {
                    e.stopPropagation();
                    if (packageDetailsSidebar && packageDetailsBody) {
                        const detailTemplate = document.getElementById('template-package-detail-card');
                        if (detailTemplate) {
                            packageDetailsBody.innerHTML = '';
                            const detailHtml = Mustache.render(detailTemplate.innerHTML, pkg);
                            const detailTempDiv = document.createElement('div');
                            detailTempDiv.innerHTML = detailHtml;
                            const detailCard = detailTempDiv.firstElementChild;

                            if (pkg.hasCountries) {
                                const countriesSection = document.createElement('div');
                                countriesSection.className = 'package-countries-section pt-2 mt-2';
                                countriesSection.innerHTML = `<h4>${i18n.t('countriesIncluded')}</h4><div class="package-countries-list d-flex flex-wrap gap-1"></div>`;

                                const countriesList = countriesSection.querySelector('.package-countries-list');
                                const countryTagTemplate = document.getElementById('template-country-tag');
                                if (countryTagTemplate) {
                                    const countryTagTemplateHtml = countryTagTemplate.innerHTML;

                                    pkg.countries.forEach(country => {
                                        const countryData = {
                                            code: country.code,
                                            name: translateDayText(country.name),
                                            flagIcon: country.flagIcon
                                        };

                                        const countryHtml = Mustache.render(countryTagTemplateHtml, countryData);
                                        const countryTempDiv = document.createElement('div');
                                        countryTempDiv.innerHTML = countryHtml;
                                        const countryTag = countryTempDiv.firstElementChild;
                                        const removeButton = countryTag.querySelector('.tag-remove');
                                        if (removeButton) {
                                            removeButton.remove();
                                        }

                                        countriesList.appendChild(countryTag);
                                    });

                                    detailCard.appendChild(countriesSection);
                                }
                            }

                            packageDetailsBody.appendChild(detailCard);
                            i18n.updatePage();
                            packageDetailsSidebar.classList.remove('hidden');
                            if (sidebarOverlay) {
                                sidebarOverlay.classList.remove('hidden');
                            }
                        }
                    }
                });
            }

            topupPlansContainer.appendChild(card);
        });

        i18n.updatePage();
        topupPlansSection.classList.remove('hidden');
        topupPlansSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    return {
        init,
        loadCountries,
        addCountryTag,
        removeCountryTag,
        renderTags,
        filterAndDisplayPlans,
        renderMatchingPlans,
        selectLocation,
        selectDuration,
        showPackageDetailsSidebar,
        closePackageDetailsSidebarFunc,
        showSuggestions,
        addPackageToCart,
        refreshCurrentSections,
        showTopupPlans
    };
}

